<template>
  <b-modal
    ref="dfCorrectionModal"
    size="lg"
    centered
    no-close-on-backdrop
    @hide="closeModal"
    @hidden="resetData"
  >
    <template #modal-title>
      <i class="fas fa-edit mr-2"></i>แก้ไข DF
    </template>

    <b-card class="mb-3" no-body>
      <b-card-header class="py-2 bg-light">ข้อมูลปัจจุบัน</b-card-header>
      <b-card-body class="py-2">
        <b-row>
          <b-col cols="6"
            ><strong>คนไข้:</strong>
            {{ currentItem.patientFullName }}</b-col
          >
          <b-col cols="6"
            ><strong>DN:</strong> {{ currentItem.dn }}</b-col
          >
        </b-row>
        <b-row class="mt-1">
          <b-col cols="6"
            ><strong>รายการรักษา:</strong>
            {{ currentItem.itemName }}</b-col
          >
          <b-col cols="6"
            ><strong>วันที่:</strong>
            {{ formatDate(currentItem.creationDt) }}</b-col
          >
        </b-row>
        <b-row class="mt-1">
          <b-col cols="4"
            ><strong>ชำระ:</strong> {{ currentItem.paid }}</b-col
          >
          <b-col cols="4"
            ><strong>DF ปัจจุบัน:</strong>
            {{ currentItem.finalDf }}</b-col
          >
          <b-col cols="4"
            ><strong>CF ปัจจุบัน:</strong>
            {{ currentItem.finalCf }}</b-col
          >
        </b-row>
      </b-card-body>
    </b-card>

    <b-card no-body>
      <b-card-header class="py-2 bg-light">ข้อมูลแก้ไข</b-card-header>
      <b-card-body class="py-2">
        <b-form-group
          label="แพทย์:"
          label-cols-md="3"
          label-align-md="right"
          class="mb-2"
          label-size="sm"
        >
          <b-form-select
            v-model="form.correctedActionUid"
            :options="doctorOptions"
            text-field="fullName"
            value-field="uid"
            size="sm"
          >
            <template #first>
              <b-form-select-option :value="null" disabled
                >-- เลือกแพทย์ --</b-form-select-option
              >
            </template>
          </b-form-select>
        </b-form-group>

        <b-form-group
          label="ประเภท DF:"
          label-cols-md="3"
          label-align-md="right"
          class="mb-2"
          label-size="sm"
        >
          <b-form-radio-group v-model="form.correctedDfType" size="sm">
            <b-form-radio :value="0">เปอร์เซ็นต์ (%)</b-form-radio>
            <b-form-radio :value="1">จำนวนเงิน (฿)</b-form-radio>
          </b-form-radio-group>
        </b-form-group>

        <b-form-group
          label="DF ใหม่:"
          label-cols-md="3"
          label-align-md="right"
          class="mb-2"
          label-size="sm"
          :invalid-feedback="dfInvalidFeedback"
          :state="dfState"
        >
          <b-input-group
            :append="form.correctedDfType === 0 ? '%' : '฿'"
            size="sm"
          >
            <b-form-input
              v-model.number="form.correctedDf"
              type="number"
              size="sm"
              :state="dfState"
              min="0"
              :max="form.correctedDfType === 0 ? 100 : undefined"
              placeholder="ระบุค่า DF ใหม่"
            />
          </b-input-group>
          <small
            v-if="dfExceedsPriceNotice"
            class="text-info d-block mt-1"
          >
            <i class="fas fa-info-circle mr-1"></i>
            ค่าหมอมากกว่ายอดในใบเสร็จ — คลินิกจะรับภาระส่วนต่าง
            ({{ formatMoney(clinicAbsorbedAmount) }} ฿)
          </small>
          <small
            v-if="percentageOnZeroPriceNotice"
            class="text-warning d-block mt-1"
          >
            <i class="fas fa-exclamation-triangle mr-1"></i>
            รายการนี้มียอด 0 บาท — ใช้ประเภทเปอร์เซ็นต์จะได้ DF = 0
            กรุณาเลือกประเภท "จำนวนเงิน (฿)"
          </small>
        </b-form-group>

        <b-form-group
          label="หมายเหตุ*:"
          label-cols-md="3"
          label-align-md="right"
          class="mb-2"
          label-size="sm"
          :invalid-feedback="'กรุณาระบุหมายเหตุ'"
          :state="remarkState"
        >
          <b-form-textarea
            v-model="form.remark"
            size="sm"
            rows="2"
            :state="remarkState"
            placeholder="ระบุเหตุผลในการแก้ไข (จำเป็น)"
          />
        </b-form-group>
      </b-card-body>
    </b-card>

    <template #modal-footer>
      <b-button variant="secondary" @click="hide">ยกเลิก</b-button>
      <b-button
        variant="primary"
        :disabled="!isFormValid || isSaving"
        @click="save"
      >
        <b-spinner v-if="isSaving" small class="mr-1" />
        บันทึก
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import moment from "moment";

export default {
  name: "DfCorrectionModal",
  data() {
    return {
      currentItem: {},
      doctorOptions: [],
      originalActionUid: null,
      form: {
        correctedDf: null,
        correctedDfType: 0,
        correctedActionUid: null,
        remark: "",
      },
      isSaving: false,
      submitted: false,
    };
  },
  computed: {
    dfState() {
      if (!this.submitted) return null;
      if (this.form.correctedDf === null || this.form.correctedDf === "")
        return false;
      if (this.form.correctedDf < 0) return false;
      if (this.form.correctedDfType === 0 && this.form.correctedDf > 100)
        return false;
      return true;
    },
    dfInvalidFeedback() {
      if (this.form.correctedDf === null || this.form.correctedDf === "")
        return "กรุณาระบุค่า DF";
      if (this.form.correctedDf < 0) return "DF ต้องไม่ต่ำกว่า 0";
      if (this.form.correctedDfType === 0 && this.form.correctedDf > 100)
        return "เปอร์เซ็นต์ต้องไม่เกิน 100";
      return "";
    },
    remarkState() {
      if (!this.submitted) return null;
      return this.form.remark.trim().length > 0;
    },
    isFormValid() {
      const dfValid =
        this.form.correctedDf !== null &&
        this.form.correctedDf !== "" &&
        this.form.correctedDf >= 0 &&
        (this.form.correctedDfType !== 0 || this.form.correctedDf <= 100);
      const remarkValid = this.form.remark.trim().length > 0;
      return dfValid && remarkValid;
    },
    itemPrice() {
      return Number(this.currentItem.price) || 0;
    },
    itemQuantity() {
      return Number(this.currentItem.quantity) || 1;
    },
    // Total fixed-baht DF the clinic is being asked to assign (df × quantity).
    fixedDfTotal() {
      if (this.form.correctedDfType !== 1) return 0;
      const df = Number(this.form.correctedDf) || 0;
      return df * this.itemQuantity;
    },
    clinicAbsorbedAmount() {
      return Math.max(0, this.fixedDfTotal - this.itemPrice);
    },
    dfExceedsPriceNotice() {
      return (
        this.form.correctedDfType === 1 &&
        this.fixedDfTotal > this.itemPrice
      );
    },
    percentageOnZeroPriceNotice() {
      return this.form.correctedDfType === 0 && this.itemPrice === 0;
    },
  },
  methods: {
    show(item, doctorsList) {
      this.currentItem = { ...item };
      this.doctorOptions = doctorsList || [];
      const matchedDoctor = this.doctorOptions.find(
        d => d.fullName === item.doctorFullName
      );
      const doctorUid = matchedDoctor ? matchedDoctor.uid : null;
      this.originalActionUid = doctorUid;
      this.form.correctedActionUid = doctorUid;
      this.$refs.dfCorrectionModal.show();
    },
    hide() {
      this.$refs.dfCorrectionModal.hide();
    },
    closeModal() {},
    resetData() {
      this.currentItem = {};
      this.form = {
        correctedDf: null,
        correctedDfType: 0,
        correctedActionUid: null,
        remark: "",
      };
      this.submitted = false;
      this.isSaving = false;
    },
    formatDate(date) {
      if (date) {
        return moment(date).locale("th").format("D MMM YY");
      }
      return "";
    },
    formatMoney(num) {
      return Number(num || 0).toLocaleString("th-TH", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    async save() {
      this.submitted = true;
      if (!this.isFormValid) return;

      this.isSaving = true;
      const payload = {
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
        receiptId: this.currentItem.receiptId || this.currentItem.id,
        receiptItemId: this.currentItem.receiptItemId,
        correctedDf: this.form.correctedDf,
        correctedDfType: this.form.correctedDfType,
        remark: this.form.remark,
      };
      if (this.form.correctedActionUid !== this.originalActionUid) {
        payload.correctedActionUid = this.form.correctedActionUid;
      }

      try {
        await this.$store.dispatch(
          "moduleDfCorrection/createDfCorrection",
          payload
        );
        this.$emit("correctionSaved");
        this.hide();
      } catch (err) {
        const status = err?.response?.status;
        if (status === 400) {
          const msg =
            err?.response?.data?.message ||
            "ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบอีกครั้ง";
          this.$emit("error", msg);
        } else if (status === 409) {
          this.$emit("error", "มีการแก้ไขซ้ำ กรุณาลองใหม่อีกครั้ง");
        } else {
          this.$emit("error", "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
        }
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>
